import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGear,
  faUtensils,
  faList,
  faLocationDot,
  faLeaf,
  faTag,
  faClipboardList,
  faChartLine,
  faDice,
  faXmark,
  faTriangleExclamation,
  faArrowRightArrowLeft,
} from '@fortawesome/free-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import './AdminSidebar.scss'
import ConfirmDialog from '../confirm-dialog/ConfirmDialog'
import {
  useGetAppStatusQuery,
  useUpdateAppStatusMutation,
} from '../../store/api/appStatusApi'
import { IAppStatus } from '../../types/appStatus'
import { getAppType } from '../../utils/appType'

type Props = {
  onClose?: () => void
}

const c = 'admin-sidebar'

interface StatusToggleConfig {
  key: keyof IAppStatus
  label: string
  icon: IconDefinition
  onColor: string
  confirmOn: string
  confirmOff: string
  conflictKey?: keyof IAppStatus
}

const STATUS_TOGGLES: StatusToggleConfig[] = [
  {
    key: 'isMaintenanceMode',
    label: 'Maintenance mode',
    icon: faTriangleExclamation,
    onColor: '#f59e0b',
    confirmOn:
      'Activezi maintenance mode. Toți utilizatorii vor vedea pagina de mentenanță.',
    confirmOff:
      'Dezactivezi maintenance mode. Aplicația va fi accesibilă din nou.',
  },
  {
    key: 'isSushiDisabled',
    label: 'Dezactivează Sushi',
    icon: faUtensils,
    onColor: '#ef4444',
    conflictKey: 'isCateringDisabled',
    confirmOn:
      'Dezactivezi PandaSushi. Butonul va fi blocat în pagina principală.',
    confirmOff: 'Activezi PandaSushi din nou.',
  },
  {
    key: 'isCateringDisabled',
    label: 'Dezactivează Catering',
    icon: faLeaf,
    onColor: '#8b5cf6',
    conflictKey: 'isSushiDisabled',
    confirmOn:
      'Dezactivezi PandaCatering. Butonul va fi blocat în pagina principală.',
    confirmOff: 'Activezi PandaCatering din nou.',
  },
]

const links = [
  { id: 0, title: 'General', link: 'general', icon: faGear },
  { id: 1, title: 'Produse', link: 'products', icon: faUtensils },
  { id: 2, title: 'Categorii', link: 'categories', icon: faList },
  { id: 3, title: 'Locatii', link: 'locations', icon: faLocationDot },
  { id: 4, title: 'Ingrediente', link: 'ingredients', icon: faLeaf },
  { id: 5, title: 'Costuri produse', link: 'costs', icon: faTag },
  { id: 6, title: 'Comenzi', link: 'orders', icon: faClipboardList },
  { id: 7, title: 'Statistici', link: 'statistics', icon: faChartLine },
  { id: 8, title: 'Ruleta', link: 'roulette', icon: faDice },
]

const AdminSidebar = ({ onClose }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [pendingEdit, setPendingEdit] = useState<{
    key: keyof IAppStatus
    newValue: boolean
    config: StatusToggleConfig
  } | null>(null)

  const { data: appStatus } = useGetAppStatusQuery()
  const [updateAppStatus, { isLoading }] = useUpdateAppStatusMutation()

  const handleToggleClick = (
    config: StatusToggleConfig,
    currentValue: boolean,
  ) => {
    setPendingEdit({ key: config.key, newValue: !currentValue, config })
  }

  const handleConfirm = async () => {
    if (pendingEdit) {
      await updateAppStatus({ [pendingEdit.key]: pendingEdit.newValue })
    }
    setPendingEdit(null)
  }

  const currentApp = getAppType()
  const otherApp = currentApp === 'sushi' ? 'catering' : 'sushi'
  const otherAppLabel = currentApp === 'sushi' ? 'PandaCatering' : 'PandaSushi'

  const handleSwitchApp = () => {
    const currentPath = location.pathname
    const adminSubPath = currentPath.split('/admin')[1] || '/general'
    window.location.href = `/${otherApp}/admin${adminSubPath}`
  }

  const logoSrc = currentApp === 'catering' ? '/panda-catering-logo-no-bg.png' : '/logo_bg.png'

  return (
    <nav className={c}>
      <div className={`${c}__header`}>
        <div className={`${c}__header-brand`}>
          <img
            src={logoSrc}
            alt='logo'
            className={`${c}__header-brand-logo`}
          />
          <span className={`${c}__header-brand-name`}>Admin Panel</span>
        </div>
        {onClose && (
          <button className={`${c}__header-close`} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
      <div className={`${c}__menu`}>
        {links.map((link) => (
          <div
            key={link.id}
            className={`${c}__menu-item ${
              location.pathname.includes(link.link) ? 'active' : ''
            }`}
            onClick={() => {
              navigate(link.link)
              onClose?.()
            }}
          >
            <FontAwesomeIcon
              icon={link.icon}
              className={`${c}__menu-item-icon`}
            />
            <span>{link.title}</span>
          </div>
        ))}
      </div>

      <div className={`${c}__switch-app`} onClick={handleSwitchApp}>
        <FontAwesomeIcon icon={faArrowRightArrowLeft} />
        <span>{otherAppLabel} Admin</span>
      </div>

      <div className={`${c}__status-controls`}>
        <span className={`${c}__status-title`}>Stare aplicație</span>
        {STATUS_TOGGLES.map((cfg) => {
          const isOn = appStatus?.[cfg.key] ?? false
          const isConflictBlocked =
            !isOn &&
            !!cfg.conflictKey &&
            !!(appStatus?.[cfg.conflictKey] ?? false)
          return (
            <div key={cfg.key} className={`${c}__status-row`}>
              <div className={`${c}__status-row-label`}>
                <FontAwesomeIcon icon={cfg.icon} />
                <span>{cfg.label}</span>
              </div>
              <button
                className={`${c}__toggle ${isOn ? 'on' : ''}`}
                style={isOn ? { backgroundColor: cfg.onColor } : undefined}
                onClick={() =>
                  !isConflictBlocked && handleToggleClick(cfg, isOn)
                }
                disabled={isLoading || isConflictBlocked}
                title={
                  isConflictBlocked
                    ? 'Nu poți dezactiva ambele aplicații simultan'
                    : undefined
                }
                aria-label={cfg.label}
              >
                <span className={`${c}__toggle-thumb`} />
              </button>
            </div>
          )
        })}
      </div>

      {pendingEdit && (
        <ConfirmDialog
          open
          title='Confirmare'
          message={
            pendingEdit.newValue
              ? pendingEdit.config.confirmOn
              : pendingEdit.config.confirmOff
          }
          onConfirm={handleConfirm}
          onCancel={() => setPendingEdit(null)}
        />
      )}
    </nav>
  )
}

export default AdminSidebar
